import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SponsorContact } from './SponsorContact';
import { DownlineTreeView } from './DownlineTreeView';
import { ReferralLinksCard } from './ReferralLinksCard';
import { CreateUserModal } from '../affiliate/CreateUserModal';
import { getCurrentUser } from '../../lib/supabase';

interface ReferralCenterProps {
  userId: number;
  className?: string;
}

export const ReferralCenter: React.FC<ReferralCenterProps> = ({ userId, className = '' }) => {
  const { t } = useTranslation('common');
  const [activeTab, setActiveTab] = useState<'sponsor' | 'downline' | 'overview'>('overview');
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [affiliateUser, setAffiliateUser] = useState<any>(null);

  // Load affiliate user data for CreateUserModal
  React.useEffect(() => {
    const loadUser = async () => {
      const user = await getCurrentUser();
      setAffiliateUser(user?.database_user || user);
    };
    loadUser();
  }, []);

  const tabs = [
    {
      id: 'overview',
      label: `📊 ${t('referrals.tabs.overview', 'Overview')}`,
      description: t('referrals.tabs.overviewDesc', 'Referral summary and quick stats'),
      icon: '📊'
    },
    {
      id: 'sponsor',
      label: `👥 ${t('referrals.tabs.sponsor', 'My Sponsor')}`,
      description: t('referrals.tabs.sponsorDesc', 'Contact and view sponsor information'),
      icon: '👥'
    },
    {
      id: 'downline',
      label: `🌳 ${t('referrals.tabs.team', 'My Team')}`,
      description: t('referrals.tabs.teamDesc', 'View and manage your referral network'),
      icon: '🌳'
    }
  ];

  const renderOverview = () => (
    <div className="space-y-4 sm:space-y-6">
      {/* Quick Stats */}
      <div className="flex flex-col gap-4 sm:gap-6 lg:grid lg:grid-cols-3">
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 text-center">
          <div className="text-3xl mb-2">👥</div>
          <h3 className="text-white text-lg font-semibold mb-2">{t('referrals.overview.yourSponsor', 'Your Sponsor')}</h3>
          <p className="text-white text-sm mb-4">
            {t('referrals.overview.yourSponsorDesc', 'Get support and guidance from your sponsor')}
          </p>
          <button
            onClick={() => setActiveTab('sponsor')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors w-full"
          >
            {t('referrals.overview.contactSponsor', 'Contact Sponsor')}
          </button>
        </div>

        <div className="bg-gray-800 rounded-lg border border-gray-700 p-6 text-center">
          <div className="text-3xl mb-2">🌳</div>
          <h3 className="text-white text-lg font-semibold mb-2">{t('referrals.overview.yourTeam', 'Your Team')}</h3>
          <p className="text-white text-sm mb-4">
            {t('referrals.overview.yourTeamDesc', 'View and manage your referral network')}
          </p>
          <button
            onClick={() => setActiveTab('downline')}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors w-full"
          >
            {t('referrals.overview.viewTeam', 'View Team')}
          </button>
        </div>

        <ReferralLinksCard userId={userId} />
      </div>

      {/* Quick Actions */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <h3 className="text-white text-xl font-semibold mb-4">🚀 {t('referrals.quickActions.heading', 'Quick Actions')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => setShowCreateUserModal(true)}
            className="bg-yellow-600 hover:bg-yellow-700 text-white p-4 rounded-lg text-center transition-colors"
          >
            <div className="text-2xl mb-2">➕</div>
            <div className="text-sm font-medium">{t('referrals.quickActions.createUser', 'Create User')}</div>
          </button>

          <button
            onClick={() => setActiveTab('sponsor')}
            className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-lg text-center transition-colors"
          >
            <div className="text-2xl mb-2">💬</div>
            <div className="text-sm font-medium">{t('referrals.quickActions.askSponsor', 'Ask Sponsor')}</div>
          </button>

          <button
            onClick={() => setActiveTab('downline')}
            className="bg-green-600 hover:bg-green-700 text-white p-4 rounded-lg text-center transition-colors"
          >
            <div className="text-2xl mb-2">👥</div>
            <div className="text-sm font-medium">{t('referrals.quickActions.contactTeam', 'Contact Team')}</div>
          </button>

          <button
            onClick={() => {
              const marketingSection = document.querySelector('[data-section="marketing"]');
              if (marketingSection) {
                marketingSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-purple-600 hover:bg-purple-700 text-white p-4 rounded-lg text-center transition-colors"
          >
            <div className="text-2xl mb-2">📱</div>
            <div className="text-sm font-medium">{t('referrals.quickActions.shareLinks', 'Share Links')}</div>
          </button>

          <button
            onClick={() => {
              const marketingSection = document.querySelector('[data-section="marketing"]');
              if (marketingSection) {
                marketingSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-orange-600 hover:bg-orange-700 text-white p-4 rounded-lg text-center transition-colors"
          >
            <div className="text-2xl mb-2">✨</div>
            <div className="text-sm font-medium">{t('referrals.quickActions.aiContent', 'AI Content')}</div>
          </button>
        </div>
      </div>

      {/* Tips and Best Practices */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <h3 className="text-white text-xl font-semibold mb-4">💡 {t('referrals.tips.heading', 'Referral Success Tips')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              icon: '🎯',
              title: t('referrals.tips.tip1.title', 'Target the Right Audience'),
              description: t('referrals.tips.tip1.desc', 'Focus on people interested in gold mining and share ownership.')
            },
            {
              icon: '🤝',
              title: t('referrals.tips.tip2.title', 'Build Genuine Relationships'),
              description: t('referrals.tips.tip2.desc', 'Focus on helping others succeed rather than just making sales.')
            },
            {
              icon: '📚',
              title: t('referrals.tips.tip3.title', 'Educate Your Prospects'),
              description: t('referrals.tips.tip3.desc', 'Share knowledge about gold mining and the benefits of share ownership.')
            },
            {
              icon: '📊',
              title: t('referrals.tips.tip4.title', 'Track Your Performance'),
              description: t('referrals.tips.tip4.desc', 'Use analytics to understand what works and optimize your approach.')
            },
            {
              icon: '💬',
              title: t('referrals.tips.tip5.title', 'Stay Connected'),
              description: t('referrals.tips.tip5.desc', 'Maintain regular contact with your team and provide ongoing support.')
            },
            {
              icon: '🎓',
              title: t('referrals.tips.tip6.title', 'Keep Learning'),
              description: t('referrals.tips.tip6.desc', 'Use the training resources to improve your marketing skills continuously.')
            }
          ].map((tip, index) => (
            <div key={index} className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="text-2xl">{tip.icon}</div>
                <div>
                  <h4 className="text-white font-semibold mb-2">{tip.title}</h4>
                  <p className="text-white text-sm">{tip.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Support Resources */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        <h3 className="text-white text-xl font-semibold mb-4">🆘 {t('referrals.help.heading', 'Need Help?')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-3xl mb-2">👥</div>
            <h4 className="text-white font-medium mb-2">{t('referrals.help.sponsor.title', 'Contact Your Sponsor')}</h4>
            <p className="text-white text-sm mb-3">
              {t('referrals.help.sponsor.desc', 'Your sponsor is here to help you succeed')}
            </p>
            <button
              onClick={() => setActiveTab('sponsor')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
            >
              {t('referrals.help.sponsor.button', 'Contact Now')}
            </button>
          </div>

          <div className="text-center">
            <div className="text-3xl mb-2">✈️</div>
            <h4 className="text-white font-medium mb-2">{t('referrals.help.telegram.title', 'Telegram Support')}</h4>
            <p className="text-white text-sm mb-3">
              {t('referrals.help.telegram.desc', 'Join our community for instant help')}
            </p>
            <button
              onClick={() => window.open('https://t.me/AureusAllianceBot')}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
            >
              {t('referrals.help.telegram.button', 'Join Telegram')}
            </button>
          </div>

          <div className="text-center">
            <div className="text-3xl mb-2">📧</div>
            <h4 className="text-white font-medium mb-2">{t('referrals.help.email.title', 'Email Support')}</h4>
            <p className="text-white text-sm mb-3">
              {t('referrals.help.email.desc', 'Get help from our support team')}
            </p>
            <button
              onClick={() => window.open('mailto:support@aureus.africa')}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
            >
              {t('referrals.help.email.button', 'Send Email')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`referral-center ${className}`}>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-white text-3xl font-bold mb-2">🤝 {t('referrals.center.heading', 'Referral Center')}</h1>
        <p className="text-white">
          {t('referrals.center.subheading', 'Manage your referral network, contact your sponsor, and grow your team')}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b border-gray-700 pb-4 overflow-x-auto scrollbar-hide">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap text-sm sm:text-base flex-shrink-0 ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
            }`}
          >
            <span className="text-base sm:text-lg">{tab.icon}</span>
            <span className="hidden sm:inline">{tab.label}</span>
            <span className="sm:hidden">{tab.label.replace('📊 ', '').replace('👥 ', '').replace('🌳 ', '')}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'sponsor' && (
          <SponsorContact userId={userId} className="bg-gray-900 rounded-lg p-3 sm:p-6" />
        )}
        {activeTab === 'downline' && (
          <DownlineTreeView userId={userId} className="bg-gray-900 rounded-lg p-3 sm:p-6" />
        )}
      </div>

      {/* Create User Modal */}
      {affiliateUser && (
        <CreateUserModal
          isOpen={showCreateUserModal}
          onClose={() => setShowCreateUserModal(false)}
          onUserCreated={() => {
            // Refresh the page or reload data to show updated referral count
            window.location.reload();
          }}
          affiliateUser={affiliateUser}
        />
      )}
    </div>
  );
};
